import {
    detectionIsOn,
    detectionIsOnForStatusChange,
    getDetectionStatusUpdate,
} from '@trqk/utils';
import { startOfQuarter, subQuarters } from 'date-fns';

/**
 * Get the detection status updates from the current table data.
 * @param {DetectionTrack[]} data updated table data from the
 * ManageDetectionsTable
 * @returns {ManageDetectionsUpdate[]} updates for all status changes that resulted from the
 * user enabling / disabling detection
 */
export const getUpdates = (data: DetectionTrack[]): ManageDetectionsUpdate[] =>
    data.reduce(
        (
            acc: ManageDetectionsUpdate[],
            item: DetectionTrack,
        ): ManageDetectionsUpdate[] => {
            const { detection_history, audio_id } = item;
            const statusChange = getDetectionStatusUpdate({
                detectionHistory: detection_history,
                draftOnly: true,
            });

            if (audio_id !== null && statusChange !== null) {
                acc.push({
                    id: audio_id,
                    statusChange,
                });
            }

            return acc;
        },
        [] as ManageDetectionsUpdate[],
    );

/**
 * @typedef {GetUpdateWarningsInput}
 * @property {DetectionTrack[]} data updated table data from the
 * ManageDetectionsTable
 * @property {number} maxDetections maximum number of active detections allowed for this user
 */
interface GetUpdateWarningsInput {
    data: DetectionTrack[];
    maxDetections: number;
    nextStatus?: DetectionStatus;
}

/**
 * @typedef {GetUpdateWarningsResult}
 * @property {boolean} gapAnalysisIsUnavailable true when update(s) would cause gap analysis to
 * become unavailable for one or more tracks because they would end detection before a calendar
 * quarter had elapsed
 * @property {boolean} exceededMaxDetections true when update(s) would cause the user to exceed the
 * maximum number of active detections allowed for this user
 */
interface GetUpdateWarningsResult {
    gapAnalysisUnavailable: boolean;
    exceededMaxDetections: boolean;
}

/**
 * Returns booleans to indicate whether the user should be warned about two situations: gap analysis
 * will become unavailable or user will exceed max detections.
 * @param {GetUpdateWarningsInput} input
 * @returns {GetUpdateWarningsResult}
 */
export const getUpdateWarnings = ({
    data,
    maxDetections,
    nextStatus,
}: GetUpdateWarningsInput): GetUpdateWarningsResult => {
    let gapAnalysisUnavailable = false;
    let requestedDetections = 0;

    data.forEach(({ detection_history }: DetectionTrack): void => {
        const statusUpdate = getDetectionStatusUpdate({
            detectionHistory: detection_history,
            draftOnly: true,
        });

        // If we find a status update that would turn off detection
        if (statusUpdate !== null && !detectionIsOn(statusUpdate.status)) {
            const dateInPriorQuarter = subQuarters(
                new Date(statusUpdate.timestamp),
                1,
            );
            const startOfPriorQuarter = startOfQuarter(dateInPriorQuarter);
            const penultimateStatusChange =
                detection_history?.[detection_history.length - 2] ?? null;

            // And the prior status change enabled detection
            if (
                penultimateStatusChange !== null &&
                detectionIsOnForStatusChange(penultimateStatusChange)
            ) {
                const lastActiveDate = new Date(
                    penultimateStatusChange.timestamp,
                );

                if (
                    // And the prior status changed after the start of the prior quarter
                    lastActiveDate > startOfPriorQuarter
                ) {
                    // Then gap analysis will become unavailable
                    gapAnalysisUnavailable = true;
                }
            }
        }

        const lastStatusChange = getDetectionStatusUpdate({
            detectionHistory: detection_history,
        });

        if (lastStatusChange !== null) {
            if (detectionIsOnForStatusChange(lastStatusChange)) {
                requestedDetections += 1;
            }
        }
    });

    const exceededMaxDetections =
        detectionIsOn(nextStatus ?? null) &&
        requestedDetections > maxDetections;

    return {
        gapAnalysisUnavailable,
        exceededMaxDetections,
    };
};

/**
 *
 * @param count number of mock detection tracks to create
 * @returns array of mock {@link DetectionTrack} objects
 */
export const makeMockDetectionTracks = (count: number): DetectionTrack[] => {
    const data: DetectionTrack[] = [];

    for (let i = 0; i < count; i += 1) {
        data.push({
            id: `id_${i}`,
            user_id: `user_id_${i}`,
            audio_id: `audio_id_${i}`,
            percent_shares: null,
            title: `Title ${i}`,
            work_ids: null,
            detection_history: [],
            detection_status: 'ENABLED' as DetectionStatus,
            asset_url: `asset_url_${i}`,
            filename: `filename_${i}`,
        });
    }

    return data;
};
